import React, {SyntheticEvent} from "react";
import {observer} from "mobx-react";


import {Dropdown, Form, Loader, Radio} from "semantic-ui-react";

import {IAppStore, withStore} from "../../../stores/abstract-app-store";
import {
    LayerSelectorGroupConfiguration,
    LayerSelectorGroupOptionConfiguration
} from "../../../config/layer-selector-configuration";
import {DropdownItemProps} from "semantic-ui-react/dist/commonjs/modules/Dropdown/DropdownItem";

export interface LayerSelectorMatrixProps {
    comparisonEnabled: boolean;
    config: LayerSelectorGroupConfiguration[];
    onSelect: (groupConfig: LayerSelectorGroupConfiguration, groupId: string, value: string, ) => void;
    small?: boolean;
    standalone?: boolean;
    trailing?: boolean;
    store?: IAppStore;
}

@observer
class LayerSelectorMatrixBase extends React.Component<LayerSelectorMatrixProps, any> {
    constructor(props: LayerSelectorMatrixProps) {
        super(props);
        this.handleSelect = this.handleSelect.bind(this);
    }

    render(): React.ReactElement | null {
        const {
            config,
            trailing,
            store
        } = this.props;

        if (!store) {
            return null;
        }

        const groups: React.ReactElement[] = [];
        // get selected option of dropdowns that have a relation to other groups
        // store value for each related dropdown
        const relatedDropdownSelections: Map<string, Array<string | undefined>> = new Map();

        config.forEach((groupConfig: LayerSelectorGroupConfiguration) => {
            if (groupConfig.hidden) {
                // do not show controls for hidden groups
                return;
            }

            // option selected before?
            const selectedOptions = store.LayerSelectorStore.getSelectedGroupOptions(groupConfig, !!trailing);

            const content: any[] = [];
            if (groupConfig.formFieldType === "dropdown") {
                let dropdownOptions = store.LayerSelectorStore.dropdownOptionsForCurrentTopic?.get(groupConfig.id);

                // remember selected options for related dropdowns
                this.storeSelectionForRelatedControls(groupConfig, selectedOptions, relatedDropdownSelections);

                // filter the options with value selected in related dropdown
                dropdownOptions = this.filterValidOptions(dropdownOptions, relatedDropdownSelections, groupConfig);

                // TODO: find a way to reset the selected option of a related control when the selected value is
                //  not available anymore when parent control changes

                const key = `checkbox_${groupConfig.id}${!!trailing ? "_tailing" : ""}`;
                // options from configuration or loaded from data store
                const options: DropdownItemProps[] | undefined =
                    groupConfig.store?
                        dropdownOptions :
                        groupConfig.options.map((optionConfig: LayerSelectorGroupOptionConfiguration) => {
                            return {
                                key: optionConfig.id,
                                text: optionConfig.label,
                                value: optionConfig.id
                            } as DropdownItemProps;
                        });

                if (!options || options.length === 0) {
                    // loading options from data. show loader
                    content.push(
                        <Form.Field key={key}>
                            <Loader active inline size="tiny"/>
                        </Form.Field>
                    );
                } else {
                    // on single selection dropdowns only a string is valid as value
                    const selectedValue: string | string[] | undefined =
                        selectedOptions && Array.isArray(selectedOptions) && !groupConfig.multiple ?
                            selectedOptions[0] :
                            selectedOptions;
                    content.push(
                        <Form.Field key={key}>
                            <Dropdown
                                selection
                                label={groupConfig.label}
                                placeholder="Prozessquelle auswählen..."
                                name={groupConfig.id + (!!trailing ? "_tailing" : "")}
                                value={selectedValue}
                                multiple={groupConfig.multiple}
                                search
                                clearable
                                onChange={(event?: SyntheticEvent | string, eventData?: object | number | number[]) => {
                                    this.handleSelect(groupConfig, event, eventData);
                                }}
                                options={options}/>
                        </Form.Field>
                    );
                }
            }
            else {
                // radio or checkbox
                groupConfig.options.forEach((optionConfig: LayerSelectorGroupOptionConfiguration) => {
                    const key = `radio_${optionConfig.id}_${groupConfig.id}${!!trailing ? "_tailing" : ""}`;
                    content.push(
                        <Form.Field key={key}>
                            <Radio
                                label={optionConfig.label}
                                name={groupConfig.id + (!!trailing ? "_tailing" : "")}
                                value={optionConfig.id}
                                onChange={(event?: SyntheticEvent | string, eventData?: object | number | number[]) => {
                                    this.handleSelect(groupConfig, event, eventData);
                                }}
                                checked={selectedOptions?.includes(optionConfig.id)}
                            />
                        </Form.Field>
                    );
                });
            }

            const groupKey = `selector-option-group_${groupConfig.id}${!!trailing ? "_tailing" : ""}`;
            groups.push(
                <Form.Group
                    grouped
                    key={groupKey} >
                    <label>{this.OrientationLabel()}{groupConfig.label}</label>
                    {content}
                </Form.Group>
            );
        });
        return (
            <div className={"geo-layer-selector-select"}>
                {groups}
            </div>
        );
    }

    /**
     * Store the selected options for related dropdowns
     * @param groupConfig
     * @param selectedOptions
     * @param relatedDropdownSelections
     * @private
     */
    private storeSelectionForRelatedControls(groupConfig: LayerSelectorGroupConfiguration,
                                             selectedOptions: string[] | undefined,
                                             relatedDropdownSelections: Map<string, Array<string | undefined>>) {
        if (!selectedOptions || !groupConfig.dataFieldRelations || groupConfig.dataFieldRelations.length === 0) {
            return;
        }

        groupConfig.dataFieldRelations.forEach((relation: string) => {
            let v = selectedOptions;
            if (typeof v === "string") {
                v = [v];
            }
            // get code for this selected option
            const codes = v?.map((va) => {
                return groupConfig.options.find((oc: LayerSelectorGroupOptionConfiguration) => {
                    return oc.id === va;
                })?.code;
            });
            relatedDropdownSelections.set(relation, codes);
        });
    }

    /**
     * Filter the options for the dropdown with the selected values of related dropdowns
     * @param dropdownOptions
     * @param relatedDropdownSelections
     * @param groupConfig
     * @private
     */
    private filterValidOptions(dropdownOptions: DropdownItemProps[] | undefined,
                               relatedDropdownSelections: Map<string, Array<string | undefined>>,
                               groupConfig: LayerSelectorGroupConfiguration): DropdownItemProps[] | undefined {
        if (!dropdownOptions) {
            return;
        }

        if (!relatedDropdownSelections.has(groupConfig.id)) {
            return dropdownOptions;
        }

        return dropdownOptions.filter((option: DropdownItemProps) => {
            const relatedDropdownValues = relatedDropdownSelections.get(groupConfig.id);
            return relatedDropdownValues?.includes(option.data);
        });
    }

    /**
     * Label specifying the orientation of the two comparable maps
     */
    private OrientationLabel(): JSX.Element {
        const {
            comparisonEnabled,
            trailing,
            store
        } = this.props;

        if (!comparisonEnabled) {
            return <></>;
        }

        const orientationLabel = trailing ?
            (store?.uiStore.uiOrientation === "portrait" ? "Karte unten: " : "Karte rechts: ") :
            (store?.uiStore.uiOrientation === "portrait" ? "Karte oben: " : "Karte links: ");

        return <span className="compare-orientation-label">{orientationLabel}</span>;
    }

    private handleSelect(groupConfig: LayerSelectorGroupConfiguration,
                         event?: SyntheticEvent | string,
                         eventData?: object | number | number[]): void {
        if (event && typeof event !== "string") {
            event.preventDefault();
        }
        if (typeof eventData !== "undefined") {
            const {onSelect} = this.props;
            let name;
            let value;
            if (typeof eventData !== "number" && !Array.isArray(eventData)) {
                name = (eventData as any).name?.replace("_tailing", "");
                value = (eventData as any).value;
            } else if (typeof event === "string") {
                name = event?.replace("_tailing", "");
                value = Array.isArray(eventData) ? eventData[0] : eventData;
            }
            onSelect(groupConfig, name, value);

            if (groupConfig.zoomToFeature) {
                this.zoomToFeature(groupConfig, value);
            }
        }
    }

    private zoomToFeature(groupConfig: LayerSelectorGroupConfiguration, itemGuid: string)   {
        const {store} = this.props;

        if (!store) {
            return;
        }
        if (!groupConfig.store) {
            throw new Error("No store defined for dropdown options");
        }

        const dataStore = store.geoDataStores.get(groupConfig.store);
        dataStore?.zoomToItem(itemGuid);
    }
}

export const LayerSelectorMatrix = withStore(LayerSelectorMatrixBase);
